import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7165ak991ove783spc0';
const BG_IMAGE = 'https://bayshoreofficial.directbooking.sg/assets/additional-background-BrjziagS.jpg';

const DATES: string[] = Array.from({ length: 14 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i + 1);
  return d.toLocaleDateString('en-SG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
});

const TIMES = [
  '10:00 AM', '11:00 AM', '12:00 PM',
  '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM',
];

const OPTIONS = [
  { id: 'brochure', label: 'E-Brochure', letter: 'A' },
  { id: 'pricelist', label: 'Price List', letter: 'B' },
  { id: 'showflat', label: 'Showflat Appointment', letter: 'C' },
];

export default function EnquiryFormSection() {
  const [step, setStep] = useState<1 | 2 | 'success'>(1);
  const [interests, setInterests] = useState({ brochure: false, pricelist: false, showflat: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  const toggleInterest = (key: keyof typeof interests) => {
    setInterests((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedInterests = OPTIONS.filter((o) => interests[o.id as keyof typeof interests]).map((o) => o.label).join(', ');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!phone.trim()) { setError('Please enter your phone number.'); return; }
    if (!privacy) { setError('Please agree to the Privacy Policy.'); return; }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        interests: selectedInterests || 'General Enquiry',
        name: name.trim(),
        email: email.trim(),
        phone: `+65${phone.trim()}`,
        'appointment-date': date,
        'appointment-time': time,
        privacy: 'agreed',
      });
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStep('success');
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry-form" className="relative min-h-[700px] flex items-center overflow-hidden">
      {/* Background */}
      <img
        src={BG_IMAGE}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20 flex flex-col items-center">
        <div className="w-full max-w-2xl">
          {step === 'success' ? (
            /* Success */
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-check-line text-3xl text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                Thank You!
              </h3>
              <p className="text-white/80 text-base">
                We have received your enquiry. Our team will contact you within 24 hours.
              </p>
              <button
                onClick={() => { setStep(1); setName(''); setEmail(''); setPhone(''); setDate(''); setTime(''); setPrivacy(false); setInterests({ brochure: false, pricelist: false, showflat: false }); }}
                className="border border-white/60 text-white font-semibold px-7 py-3 rounded-full hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap text-sm mt-2"
              >
                Submit Another Enquiry
              </button>
            </div>
          ) : step === 1 ? (
            /* Step 1 */
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white leading-snug">
                Hello! Welcome to the official Vela Bay Concierge. How can we assist you today?
              </h3>
              <div className="flex flex-col gap-3">
                {OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                      interests[opt.id as keyof typeof interests]
                        ? 'border-white bg-white/20'
                        : 'border-white/30 bg-white/10 hover:border-white/60 hover:bg-white/15'
                    }`}
                  >
                    <div className="w-8 h-8 flex-shrink-0 rounded bg-white/10 border border-white/50 flex items-center justify-center text-white font-bold text-sm">
                      {opt.letter}
                    </div>
                    <span className="text-white font-medium text-base">{opt.label}</span>
                    <div className="ml-auto">
                      <input
                        type="checkbox"
                        name="interests"
                        value={opt.id}
                        checked={interests[opt.id as keyof typeof interests]}
                        onChange={() => toggleInterest(opt.id as keyof typeof interests)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        interests[opt.id as keyof typeof interests]
                          ? 'bg-white border-white'
                          : 'border-white/60'
                      }`}>
                        {interests[opt.id as keyof typeof interests] && (
                          <i className="ri-check-line text-[#172C37] text-xs" />
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="bg-white text-[#172C37] font-bold px-10 py-3 rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  OK
                </button>
              </div>
            </div>
          ) : (
            /* Step 2 */
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-white/60 hover:text-white transition-colors cursor-pointer"
                  aria-label="Back"
                >
                  <i className="ri-arrow-left-line text-lg" />
                </button>
                <h3 className="text-xl font-bold text-white">Enter Your Details</h3>
              </div>

              {selectedInterests && (
                <p className="text-white/60 text-sm">
                  Interested in: <span className="text-white font-medium">{selectedInterests}</span>
                </p>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <input
                  name="name"
                  placeholder="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="border border-white/30 bg-white/10 text-white placeholder-white/50 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-white/70 w-full"
                  required
                />
                <input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border border-white/30 bg-white/10 text-white placeholder-white/50 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-white/70 w-full"
                  required
                />
              </div>

              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-sm text-white/70 pointer-events-none">+65</span>
                <input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="border border-white/30 bg-white/10 text-white placeholder-white/50 rounded-full pl-12 pr-5 py-3 text-sm focus:outline-none focus:border-white/70 w-full"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 relative">
                {/* Date Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setDateOpen(!dateOpen); setTimeOpen(false); }}
                    className="w-full border border-white/30 bg-white/10 text-left rounded-full px-5 py-3 text-sm focus:outline-none cursor-pointer flex items-center justify-between"
                  >
                    <span className={date ? 'text-white' : 'text-white/50'}>
                      {date || 'Choose date'}
                    </span>
                    <i className="ri-arrow-down-s-line text-white/60 text-base" />
                  </button>
                  {dateOpen && (
                    <div className="absolute top-full mt-1 left-0 right-0 bg-[#172C37] border border-white/20 rounded-2xl z-20 max-h-48 overflow-y-auto">
                      {DATES.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => { setDate(d); setDateOpen(false); }}
                          className="w-full text-left px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors cursor-pointer"
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Time Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setTimeOpen(!timeOpen); setDateOpen(false); }}
                    className="w-full border border-white/30 bg-white/10 text-left rounded-full px-5 py-3 text-sm focus:outline-none cursor-pointer flex items-center justify-between"
                  >
                    <span className={time ? 'text-white' : 'text-white/50'}>
                      {time || 'Choose time'}
                    </span>
                    <i className="ri-arrow-down-s-line text-white/60 text-base" />
                  </button>
                  {timeOpen && (
                    <div className="absolute top-full mt-1 left-0 right-0 bg-[#172C37] border border-white/20 rounded-2xl z-20 max-h-48 overflow-y-auto">
                      {TIMES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => { setTime(t); setTimeOpen(false); }}
                          className="w-full text-left px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors cursor-pointer"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-white/60 text-xs">Showflat viewing is strictly by advance appointment only.</p>

              {/* Privacy Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <div
                  onClick={() => setPrivacy(!privacy)}
                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                    privacy ? 'bg-white border-white' : 'border-white/50'
                  }`}
                >
                  {privacy && <i className="ri-check-line text-[#172C37] text-xs" />}
                </div>
                <input type="hidden" name="privacy" value={privacy ? 'agreed' : ''} />
                <p className="text-white/70 text-xs leading-relaxed">
                  By submitting this form, you agree to our{' '}
                  <a
                    href="https://book-showflat.sg/privacy-policy"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="underline text-white/90"
                  >
                    Privacy Policy
                  </a>{' '}
                  and consent that we or our sales representative may contact you for the purpose of
                  your enquiry via voice call, email, SMS, WhatsApp, regardless of your DNS registry
                  status. (PDPA 2012)
                </p>
              </label>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-white text-[#172C37] font-bold py-4 rounded-full hover:bg-white/90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Enquire Now'}
              </button>
              <p className="text-white/50 text-xs text-center">
                Please wait after clicking the submit button while we process your response.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
